import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckOutlined,
  FileProtectOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Dropdown, Space, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useInterval } from 'react-use'
import { PageContainer, PageHeader } from '../../../../components/page'
import * as constants from './constants'
import * as store from './store'
import * as utils from './utils'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'
import type { X937File } from '../../../../domain/x937'
import $api from '../../../../API'
import type { X937FilesResponse } from './types'
import { X937FileExpandableRow } from './components/expandable-row'

export const X937ChecksPage = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()

  const initialState = useMemo(() => {
    return {
      ...store.initialState,
      ...utils.buildStateFromSearchParams(searchParams)
    }
  }, [searchParams])

  const [state, dispatch] = useReducer(store.reducer, initialState)

  const x937FilesQuery = useQuery({
    queryKey: [
      'x937-files',
      {
        currentPage: state.pagination.currentPage,
        pageSize: state.pagination.pageSize
      }
    ],

    queryFn: async ({ queryKey }) => {
      const { currentPage, pageSize } = queryKey[1] as typeof queryKey extends Array<
        string | infer T
      >
        ? T
        : never

      const searchParams = new URLSearchParams([
        ['page', currentPage.toString()],
        ['page-size', pageSize.toString()]
      ])

      const result = await $api.get<X937FilesResponse>(
        `/pinnacle/x937/files?${searchParams.toString()}`
      )

      return result.data
    },

    keepPreviousData: true
  })

  useEffect(() => {
    const params = utils.buildSearchParams([
      {
        name: 'page',
        value: state.pagination.currentPage
      },
      {
        name: 'page-size',
        value: state.pagination.pageSize
      }
    ])

    setSearchParams(params)
  }, [state.pagination, setSearchParams])

  const handleRefresh = useCallback(() => {
    void x937FilesQuery.refetch()
  }, [x937FilesQuery])

  useInterval(() => {
    void x937FilesQuery.refetch()
  }, state.refreshInterval ?? null)

  const tableColumns = useMemo<ColumnsType<X937File>>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 300,

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Name',
        dataIndex: 'originalFileName',
        key: 'originalFileName',
        width: 300,

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Creation Date',
        dataIndex: 'fileCreationDate',
        key: 'fileCreationDate',

        render(value: string) {
          return dayjs(value).format('MM/DD/YYYY')
        },

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Extraction Date',
        dataIndex: 'createdAt',
        key: 'createdAt',

        render(value: string) {
          return dayjs(value).tz('America/Chicago').format('MM/DD/YYYY HH:mm:ss')
        },

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Bundles',
        dataIndex: 'bundleCount',
        key: 'bundleCount',

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Checks',
        dataIndex: 'checkCount',
        key: 'checkCount',

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Images',
        dataIndex: 'imageCount',
        key: 'imageCount',

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Total Amount',
        dataIndex: 'totalAmountCents',
        key: 'totalAmountCents',
        align: 'right',

        render(value: number) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(value / 100)
        },

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      }
    ],
    []
  )

  return (
    <PageContainer>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <PageHeader title='Paid Checks x937' icon={<FileProtectOutlined />} />

        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-row gap-2 items-center p-4'>
            <div className='flex-grow' />

            <Space>
              <Dropdown.Button
                type='default'
                disabled={x937FilesQuery.isFetching}
                onClick={handleRefresh}
                icon={
                  <CaretDownOutlined
                    className={cx(
                      'w-[12px] h-[12px] !translate-y-[2px]',
                      typeof state.refreshInterval === 'number' && 'text-lucent-primary',
                      typeof state.refreshInterval !== 'number' && 'text-lucent-gray'
                    )}
                  />
                }
                menu={{
                  items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                    key: `refresh-${seconds}`,
                    label: utils.formatDuration(seconds * 1000),

                    itemIcon() {
                      if (state.refreshInterval === seconds * 1000) {
                        return <CheckOutlined className='ml-2 text-lucent-primary' />
                      }

                      return null
                    },

                    onClick() {
                      if (state.refreshInterval === seconds * 1000) {
                        dispatch({
                          type: 'refreshInterval/clear'
                        })

                        return
                      }

                      dispatch({
                        type: 'refreshInterval/set',
                        payload: seconds * 1000
                      })
                    }
                  }))
                }}
              >
                <div className='flex items-center space-x-1.5'>
                  <SyncOutlined
                    className={cx(
                      'text-base',
                      x937FilesQuery.isFetching && 'animate-custom-spin text-lucent-gray',
                      !x937FilesQuery.isFetching && 'text-lucent-primary'
                    )}
                  />

                  <span
                    className={cx(
                      'font-lato font-bold text-[10px] uppercase',
                      x937FilesQuery.isFetching && 'text-lucent-gray',
                      !x937FilesQuery.isFetching && 'text-lucent-primary'
                    )}
                  >
                    <span>Refresh</span>

                    {typeof state.refreshInterval === 'number' && (
                      <span>
                        <span className='mx-1.5'>|</span>
                        Auto {utils.formatDuration(state.refreshInterval, { short: true })}
                      </span>
                    )}
                  </span>
                </div>
              </Dropdown.Button>
            </Space>
          </div>

          <Table
            size='small'
            columns={tableColumns}
            rowKey='id'
            dataSource={x937FilesQuery.data?.data ?? []}
            loading={x937FilesQuery.isFetching}
            className='rounded-none border-t payment-files-table'
            pagination={{
              current: state.pagination.currentPage + 1,
              pageSize: state.pagination.pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              total: x937FilesQuery.data?.total ?? 0,
              showTotal(total, [from, to]) {
                const parts = [
                  `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                ]

                if (
                  typeof x937FilesQuery.data?.total === 'number' &&
                  x937FilesQuery.data.total !== total
                ) {
                  parts.push(
                    `Total ${x937FilesQuery.data.total} ${x937FilesQuery.data.total === 1 ? 'item' : 'items'}`
                  )
                }

                return <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
              },
              hideOnSinglePage: false
            }}
            onChange={(pagination, _filters, _sorters, extra) => {
              if (extra.action === 'paginate') {
                dispatch({
                  type: 'pagination/currentPage/set',
                  payload: (pagination.current ?? 1) - 1
                })

                if ((pagination.pageSize ?? 10) !== state.pagination.pageSize) {
                  dispatch({
                    type: 'pagination/pageSize/set',
                    payload: pagination.pageSize ?? 10
                  })
                }
              }
            }}
            expandable={{
              expandedRowRender: file => <X937FileExpandableRow fileId={file.id} />,
              expandRowByClick: true,
              expandIcon: ExpandIcon
            }}
          />
        </div>
      </div>
    </PageContainer>
  )
}

const ExpandIcon = (props: RenderExpandIconProps<X937File>): JSX.Element | null => {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-50 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}
